import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Sparkles, Gift, CheckCircle2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';

const WHEEL_SEGMENTS = [
    { label: '50 PTS', color: '#0B132B' },
    { label: '100 PTS', color: '#C8A96A' },
    { label: '200 PTS', color: '#0B132B' },
    { label: 'TRY AGAIN', color: '#4A4D57' },
    { label: '500 PTS', color: '#C8A96A' },
    { label: '₹100 OFF', color: '#0B132B' },
    { label: 'JACKPOT', color: '#C8A96A' },
    { label: '100 PTS', color: '#4A4D57' },
];

export default function Games() {
    const { type, loading, user, refresh } = useAuth();
    const [spinStatus, setSpinStatus] = useState({ has_spun_today: false });
    const [scratchStatus, setScratchStatus] = useState({ has_scratched_today: false });
    const [history, setHistory] = useState([]);
    const [spinning, setSpinning] = useState(false);
    const [spinAngle, setSpinAngle] = useState(0);
    const [spinResult, setSpinResult] = useState(null);
    const [scratchResult, setScratchResult] = useState(null);
    const [scratched, setScratched] = useState(false);
    const canvasRef = useRef(null);

    useEffect(() => {
        if (type === 'customer') {
            api.get('/games/spin/today').then(({ data }) => setSpinStatus(data));
            api.get('/games/scratch/today').then(({ data }) => setScratchStatus(data));
            api.get('/games/me/history').then(({ data }) => setHistory(data));
        }
    }, [type]);

    // Init scratch canvas
    useEffect(() => {
        if (!scratchStatus.has_scratched_today && canvasRef.current) {
            const c = canvasRef.current;
            const ctx = c.getContext('2d');
            ctx.fillStyle = '#0B132B';
            ctx.fillRect(0, 0, c.width, c.height);
            ctx.fillStyle = '#C8A96A';
            ctx.font = "bold 14px 'General Sans', sans-serif";
            ctx.textAlign = 'center';
            ctx.fillText('SCRATCH TO REVEAL', c.width / 2, c.height / 2 - 8);
            ctx.fillStyle = 'rgba(255,255,255,0.6)';
            ctx.font = "11px 'General Sans', sans-serif";
            ctx.fillText('Drag your finger or mouse here', c.width / 2, c.height / 2 + 14);
        }
    }, [scratchStatus.has_scratched_today, scratchResult]);

    if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
    if (type !== 'customer') return <Navigate to="/login?next=/games" replace />;

    // SPIN
    const spin = async () => {
        if (spinStatus.has_spun_today || spinning) return;
        setSpinning(true);
        try {
            const { data } = await api.post('/games/spin');
            // animate: extra spins + offset to land on a fitting segment
            const idx = WHEEL_SEGMENTS.findIndex((s) => s.label.includes(data.prize.label.split(' ')[0])) ?? 0;
            const segAngle = 360 / WHEEL_SEGMENTS.length;
            const target = 720 + (360 - idx * segAngle - segAngle / 2);
            setSpinAngle(target);
            setTimeout(async () => {
                setSpinResult(data.prize);
                setSpinStatus({ has_spun_today: true, last_play: data.play });
                setSpinning(false);
                toast.success(`You won: ${data.prize.label}`);
                await refresh();
                const h = await api.get('/games/me/history');
                setHistory(h.data);
            }, 4100);
        } catch (e) {
            setSpinning(false);
            toast.error(e?.response?.data?.detail || 'Could not spin');
        }
    };

    // SCRATCH
    const doScratch = async () => {
        if (scratchStatus.has_scratched_today || scratchResult) return;
        try {
            const { data } = await api.post('/games/scratch');
            setScratchResult(data.prize);
            toast.success(`You won: ${data.prize.label}`);
            await refresh();
            const h = await api.get('/games/me/history');
            setHistory(h.data);
            setScratchStatus({ has_scratched_today: true });
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not scratch');
        }
    };

    const scratchAt = (e) => {
        if (!canvasRef.current || scratched || scratchStatus.has_scratched_today) return;
        const rect = canvasRef.current.getBoundingClientRect();
        const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
        const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
        const ctx = canvasRef.current.getContext('2d');
        ctx.globalCompositeOperation = 'destination-out';
        ctx.beginPath();
        ctx.arc(x, y, 25, 0, Math.PI * 2);
        ctx.fill();
        // count erased pixels (sample)
        const data = ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height).data;
        let erased = 0;
        for (let i = 3; i < data.length; i += 100) if (data[i] === 0) erased++;
        const total = data.length / 4 / 25;
        if (erased / total > 0.4) {
            setScratched(true);
            doScratch();
        }
    };

    // DAILY CHECK-IN
    const checkin = async () => {
        try {
            const { data } = await api.post('/games/checkin');
            toast.success(`+${data.points_won} points — see you tomorrow!`);
            await refresh();
            const h = await api.get('/games/me/history');
            setHistory(h.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Already checked in');
        }
    };

    const today = new Date().toISOString().slice(0, 10);
    const alreadyCheckedIn = history.some((h) => h.game_type === 'daily_checkin' && h.played_at?.startsWith(today));

    const segAngle = 360 / WHEEL_SEGMENTS.length;

    return (
        <div className="pt-16 pb-32 bg-cream min-h-screen" data-testid="games-page">
            <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 mb-12">
                <div className="overline mb-4">Play & Win</div>
                <h1 className="font-serif text-5xl md:text-6xl text-navy leading-tight">Daily games. <span className="italic text-gold">Real rewards.</span></h1>
            </section>

            <section className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-3 gap-8">
                {/* SPIN WHEEL */}
                <div className="card-luxury p-10 text-center" data-testid="spin-wheel-card">
                    <Sparkles size={28} className="text-gold mb-4 mx-auto" strokeWidth={1.3} />
                    <div className="overline mb-2">Spin The Wheel</div>
                    <div className="font-serif text-2xl text-navy mb-8">Daily Spin</div>

                    <div className="relative w-64 h-64 mx-auto mb-6">
                        <div
                            className={`w-full h-full rounded-full relative overflow-hidden transition-transform ${spinning ? 'spin-anim' : ''}`}
                            style={{ '--spin-angle': `${spinAngle}deg`, transform: !spinning ? `rotate(${spinAngle}deg)` : undefined, transition: spinning ? 'transform 4s cubic-bezier(.17,.67,.27,1)' : 'none' }}
                        >
                            <svg viewBox="0 0 200 200" className="w-full h-full">
                                {WHEEL_SEGMENTS.map((s, i) => {
                                    const start = i * segAngle;
                                    const end = start + segAngle;
                                    const sRad = (start - 90) * Math.PI / 180;
                                    const eRad = (end - 90) * Math.PI / 180;
                                    const x1 = 100 + 100 * Math.cos(sRad);
                                    const y1 = 100 + 100 * Math.sin(sRad);
                                    const x2 = 100 + 100 * Math.cos(eRad);
                                    const y2 = 100 + 100 * Math.sin(eRad);
                                    const path = `M 100 100 L ${x1} ${y1} A 100 100 0 0 1 ${x2} ${y2} Z`;
                                    const textAngle = start + segAngle / 2;
                                    const tRad = (textAngle - 90) * Math.PI / 180;
                                    const tx = 100 + 65 * Math.cos(tRad);
                                    const ty = 100 + 65 * Math.sin(tRad);
                                    return (
                                        <g key={i}>
                                            <path d={path} fill={s.color} />
                                            <text x={tx} y={ty} textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="8" fontWeight="600" transform={`rotate(${textAngle} ${tx} ${ty})`}>{s.label}</text>
                                        </g>
                                    );
                                })}
                            </svg>
                            <div className="absolute inset-0 rounded-full border-4 border-navy" />
                        </div>
                        {/* pointer */}
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1" style={{ width: 0, height: 0, borderLeft: '12px solid transparent', borderRight: '12px solid transparent', borderTop: '20px solid #C8A96A' }} />
                        <div className="absolute inset-0 m-auto w-10 h-10 rounded-full bg-navy border-4 border-gold" style={{ top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }} />
                    </div>

                    {spinResult ? (
                        <div className="text-navy" data-testid="spin-result">
                            <div className="overline text-gold mb-1">You Won</div>
                            <div className="font-serif text-2xl">{spinResult.label}</div>
                        </div>
                    ) : (
                        <button onClick={spin} disabled={spinStatus.has_spun_today || spinning} className={spinStatus.has_spun_today ? 'btn-outline opacity-50' : 'btn-gold'} data-testid="spin-btn">
                            {spinStatus.has_spun_today ? 'Come back tomorrow' : spinning ? 'Spinning…' : 'Spin Now'}
                        </button>
                    )}
                </div>

                {/* SCRATCH CARD */}
                <div className="card-luxury p-10 text-center" data-testid="scratch-card-card">
                    <Gift size={28} className="text-gold mb-4 mx-auto" strokeWidth={1.3} />
                    <div className="overline mb-2">Scratch Card</div>
                    <div className="font-serif text-2xl text-navy mb-8">Reveal your prize</div>
                    <div className="relative w-64 h-64 mx-auto mb-6 bg-cream border border-border-medium">
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            {scratchResult || scratchStatus.has_scratched_today ? (
                                <>
                                    <div className="overline text-gold">Prize Revealed</div>
                                    <div className="font-serif text-3xl text-navy mt-2">{scratchResult?.label || 'Already used today'}</div>
                                </>
                            ) : <div className="text-text-tertiary">Hidden prize</div>}
                        </div>
                        {!scratchResult && !scratchStatus.has_scratched_today && (
                            <canvas
                                ref={canvasRef}
                                width={256}
                                height={256}
                                className="absolute inset-0 cursor-pointer"
                                onMouseMove={(e) => e.buttons === 1 && scratchAt(e)}
                                onTouchMove={scratchAt}
                                data-testid="scratch-canvas"
                            />
                        )}
                    </div>
                    {!scratchResult && !scratchStatus.has_scratched_today && (
                        <button onClick={doScratch} className="btn-outline" data-testid="scratch-btn">Or tap to reveal</button>
                    )}
                </div>

                {/* DAILY CHECK-IN */}
                <div className="card-luxury p-10 text-center flex flex-col" data-testid="daily-checkin-card">
                    <CheckCircle2 size={28} className="text-gold mb-4 mx-auto" strokeWidth={1.3} />
                    <div className="overline mb-2">Daily Check-In</div>
                    <div className="font-serif text-2xl text-navy mb-6">25 free points</div>
                    <div className="flex-1 flex items-center justify-center mb-6">
                        <div className="font-serif text-6xl text-gold">+25</div>
                    </div>
                    <p className="text-sm text-text-secondary mb-6">Check in each day and watch your points stack up.</p>
                    <button onClick={checkin} disabled={alreadyCheckedIn} className={alreadyCheckedIn ? 'btn-outline opacity-50' : 'btn-primary'} data-testid="checkin-btn">
                        {alreadyCheckedIn ? 'Checked in today' : 'Check In Now'}
                    </button>
                </div>
            </section>

            {history.length > 0 && (
                <section className="max-w-7xl mx-auto px-6 md:px-12 mt-16" data-testid="game-history-section">
                    <div className="overline mb-4">Recent Plays</div>
                    <div className="card-luxury divide-y divide-border-subtle">
                        {history.slice(0, 10).map((p) => (
                            <div key={p.id} className="px-6 py-4 flex items-center justify-between">
                                <div>
                                    <div className="text-sm text-navy capitalize">{p.game_type.replace('_', ' ')} — {p.result}</div>
                                    <div className="text-xs text-text-tertiary mt-1">{new Date(p.played_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                                </div>
                                {p.points_won > 0 && <div className="font-mono text-emerald-700">+{p.points_won}</div>}
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
